import { api } from "../../api";
import { useAsync } from "../../useAsync";
import { count, money, shortTime } from "../../format";
import { TrendChart } from "../../components/TrendChart";
import { go } from "../../router";

export function AdminOverview() {
  const overview = useAsync(() => api.admin.overview(), []);
  const recent = useAsync(() => api.admin.accounts({ sort: "created_at", dir: "desc", limit: 8 }), []);

  if (overview.error) return <Problem message={overview.error} />;
  if (overview.loading || !overview.data) return <Loading />;
  const o = overview.data;
  const t = o.totals;

  return (
    <>
      <div className="metrics" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", marginTop: 18 }}>
        <Metric label="Accounts" value={count(t.accounts)} />
        <Metric label="Users" value={count(t.users)} />
        <Metric label="Paying" value={count(t.paying)} tone={t.paying > 0 ? "var(--pos)" : undefined} />
        <Metric label="Est. MRR" value={money(o.mrr_cents)} />
        <Metric label="Trialing" value={count(t.trialing)} />
        <Metric label="Trials ending 7d" value={count(t.trials_ending_7d)}
                tone={t.trials_ending_7d > 0 ? "var(--warn)" : undefined} />
        <Metric label="Activated (source)" value={count(t.activated_connections)} />
        <Metric label="Activated (events)" value={count(t.activated_events)} />
      </div>

      <section style={{ marginTop: 34 }}>
        <h2 style={{ marginBottom: 8 }}>Signups · 90 days</h2>
        <span className="sub">New workspaces per day. People are counted separately when someone joins an existing one.</span>
        <div style={{ marginTop: 16 }}>
          <TrendChart
            values={o.signups.map((s) => s.accounts)}
            days={o.signups.map((s) => s.day)}
            isMoney={false}
          />
        </div>
      </section>

      <section style={{ marginTop: 34 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
          <h2>Latest accounts</h2>
          <button type="button" className="btn-link" onClick={() => go({ name: "admin", tab: "accounts" })}>
            All accounts
          </button>
        </div>
        {recent.error && <div className="form-error">{recent.error}</div>}
        {recent.data && (
          <div style={{ overflowX: "auto" }}>
            <table className="ptable">
              <thead>
                <tr>
                  <th>Workspace</th>
                  <th>Plan</th>
                  <th>Projects</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {recent.data.accounts.map((a) => (
                  <tr key={a.id} onClick={() => go({ name: "admin", tab: "accounts", id: a.id })}>
                    <td>
                      <div className="cell" style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
                        <span>{a.name}</span>
                        <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>{a.email}</span>
                      </div>
                    </td>
                    <td><div className="cell" style={{ textTransform: "capitalize" }}>{a.plan_status === "trialing" ? "trial" : a.plan}</div></td>
                    <td><div className="cell num">{a.products}</div></td>
                    <td><div className="cell num">{shortTime(a.created_at)}</div></td>
                  </tr>
                ))}
                {recent.data.accounts.length === 0 && (
                  <tr><td colSpan={4}><div className="cell" style={{ color: "var(--text-4)" }}>No accounts yet.</div></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <div className="eyebrow">{label}</div>
      <div className="num" style={{ fontSize: 25, letterSpacing: "-0.03em", color: tone ?? "var(--text)" }}>
        {value}
      </div>
    </div>
  );
}

function Loading() {
  return <div style={{ padding: "40px 0", color: "var(--text-faint)" }}>Loading…</div>;
}

function Problem({ message }: { message: string }) {
  return <div style={{ padding: "40px 0", color: "var(--neg)" }}>{message}</div>;
}
